import type { Collection } from '../domain/collections';
import type { PersonRef } from '../domain/people';
import { fullName } from './people';

export type OwnerGroup = { owner: PersonRef; collections: Collection[] };

export type CollectionGroups = {
  /** Mine, my default first. */
  owned: Collection[];
  /** Someone else's that I'm a member of, one group per owner. */
  member: OwnerGroup[];
  /** Someone else's open ones I see by following them, one group per owner. */
  following: OwnerGroup[];
};

/** One group per owner, owners by full name, collections in the order given. */
function byOwner(collections: Collection[]): OwnerGroup[] {
  const groups = new Map<string, OwnerGroup>();
  for (const c of collections) {
    const group = groups.get(c.owner.id) ?? { owner: c.owner, collections: [] };
    group.collections.push(c);
    groups.set(c.owner.id, group);
  }
  return [...groups.values()].sort((a, b) => fullName(a.owner).localeCompare(fullName(b.owner)));
}

/** The collections screen's three groups, from how I relate to each (015 R18). */
export function groupCollections(collections: Collection[]): CollectionGroups {
  const owned = collections.filter((c) => c.myRelation === 'owner');
  return {
    owned: [...owned.filter((c) => c.isDefault), ...owned.filter((c) => !c.isDefault)],
    member: byOwner(
      collections.filter((c) => c.myRelation === 'contributor' || c.myRelation === 'viewer'),
    ),
    following: byOwner(collections.filter((c) => c.myRelation === 'follower')),
  };
}
